/**
 * **A03 · The deposit slip cover** (Stream A, Paper.dc.html), laid out by the `count_papers`
 * engine: it goes in the night bag on top of the bank's own slip — every bag by number with its
 * area, register, what was counted and who counted it (the deposit's counts, from the day's
 * queue), the totals, two names, and the bank it goes to, read from Money's bank accounts (never
 * typed). The bank bag's serial is the slip CIDER matches to the statement. Scored by walk 99g.
 */
import type { ReactNode } from 'react';
import { depositCover } from '@cider/domain/engines';
import {
  NOTES,
  type Bag,
  type DepositRow,
  type Register,
  type Sheet,
} from '../views/money/CrShared.js';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

interface Recon {
  rows: { registerId: string; area: string | null }[];
}

export function DepositCover({ year, id }: { year: string; id: string }): ReactNode {
  const dep = useLoad<DepositRow>(`/money/countroom/deposits/${id}?year=${year}`);
  const banks = useLoad<{
    accounts: { id: string; name: string; last4: string | null }[];
    yearBankId: string | null;
  }>('/money/bank-accounts');
  const d = dep.data && 'memo' in dep.data ? dep.data : null;
  const day = d?.festivalDay ?? null;
  const queue = useLoad<{ counts: Sheet[] }>(day ? `/money/countroom/${year}/${day}/queue` : null);
  const recon = useLoad<Recon>(day ? `/money/countroom/${year}/${day}/reconciliation` : null);
  const bags = useLoad<{ bags: Bag[] }>(`/money/countroom/${year}/bags`);
  const regs = useLoad<{ registers: Register[] }>(`/money/countroom/${year}/registers`);
  const loads = day ? [banks, queue, recon, bags, regs] : [banks, bags, regs];
  const ready = !!d && loads.every((l) => l.data !== null || l.error !== null);
  usePrinted('deposit-cover', ready, { ref: id, year: Number(year) });
  if (dep.error) return <p role="alert">{dep.error}</p>;
  if (!d || !ready) return <p>Reading the deposit…</p>;
  const accounts = banks.data?.accounts ?? [];
  // Year settings names the year's bank (Rebuild 41); until it does, the operating account.
  const bank =
    accounts.find((a) => a.id === banks.data?.yearBankId) ??
    accounts.find((a) => /operating/i.test(a.name)) ??
    accounts[0];
  const counts = (queue.data?.counts ?? []).filter((c) => d.counts.includes(c.id));
  const sum = d.physical ?? d.expected;
  const page = depositCover({
    id: d.id,
    day,
    bank: bank ? `${bank.name}${bank.last4 ? ` · ${bank.last4}` : ''}` : null,
    slip: d.bankBagSerial,
    bags: counts.map((c) => ({
      bag: bags.data?.bags.find((b) => b.id === c.cashBagId)?.bagCode ?? '—',
      area: recon.data?.rows.find((r) => r.registerId === c.sourceId)?.area ?? null,
      register: regs.data?.registers.find((r) => r.id === c.sourceId)?.name ?? null,
      cents: c.grossCents,
      counters: c.counters.map((x) => x.name),
    })),
    totals: sum
      ? {
          currencyCents: NOTES.reduce((t, k) => t + sum[k] * Number(k.replace(/\D/g, '')), 0) * 100,
          coinCents: sum.coinCents,
          checkCents: sum.checkCents,
          totalCents: sum.totalCents,
        }
      : null,
  });
  return <DrawnPages pages={[page]} stamp="time" />;
}
